import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { useTDTier } from '../../hooks/useTDTier';
import TierGate from '../../components/TierGate/TierGate';
import { Box, Flex, Text, Spinner, Center, Avatar } from '@chakra-ui/react';

// ── HAVERSINE DISTANCE ─────────────────────────────────────────────────────
const haversine = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const kmToMinutes = (km: number): number => Math.round(km / 50 * 60);

const TIER_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  home_certified: { label: 'Home Game Certified', icon: '🃏', color: '#22C55E' },
  pc_certified:   { label: 'PC Certified',        icon: '🎖', color: '#19E6CF' },
  pc_senior:      { label: 'PC Senior Dealer',    icon: '⭐', color: '#A855F7' },
  pc_master:      { label: 'PC Master Dealer',    icon: '👑', color: '#D4A017' },
  pc_pro:         { label: 'PC Verified Pro',     icon: '💎', color: '#EF4444' },
};

const AVAIL_CONFIG: Record<string, { label: string; color: string }> = {
  available:     { label: 'Available',           color: '#22C55E' },
  busy:          { label: 'Busy',                color: '#F59E0B' },
  not_available: { label: 'Not Taking Bookings', color: '#6B7590' },
};

const RADIUS_OPTIONS = [25, 50, 75, 100, 9999];
const RADIUS_LABELS: Record<number, string> = { 25: '25 km', 50: '50 km', 75: '75 km', 100: '100 km', 9999: 'Province-wide' };

interface DealerResult {
  uid: string;
  displayName: string;
  photoURL: string;
  tier: string;
  track: string;
  availability: string;
  home_city: string;
  home_lat: number;
  home_lng: number;
  travel_radius_km: number;
  avg_rating: number;
  rating_count: number;
  response_rate: number;
  event_count: number;
  cancellation_count: number;
  late_cancellation_count: number;
  distance_km: number;
  games: string[];
}

interface DealerDiscoveryPageProps {
  mode?: 'browse' | 'hire';
  eventId?: string;
  eventName?: string;
  eventLat?: number;
  eventLng?: number;
  onDealerInvited?: (dealerUid: string) => void;
}

const DealerDiscoveryPage: FC<DealerDiscoveryPageProps> = ({
  mode = 'browse',
  eventId,
  eventName,
  eventLat,
  eventLng,
  onDealerInvited,
}) => {
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [searchCity, setSearchCity] = useState('');
  const [searchLat, setSearchLat] = useState<number | null>(null);
  const [searchLng, setSearchLng] = useState<number | null>(null);
  const [searchRadius, setSearchRadius] = useState(50);
  const [geocoding, setGeocoding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState<DealerResult[]>([]);
  const [searched, setSearched] = useState(false);
  const [trackFilter, setTrackFilter] = useState<'all' | 'home' | 'professional'>('all');
  const [availFilter, setAvailFilter] = useState<'all' | 'available'>('available');
  const [invitingSending, setInviteSending] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState<Set<string>>(new Set());
  const [inviteDeadline, setInviteDeadline] = useState('');
  const [isTD, setIsTD] = useState<boolean | null>(null);

  // Check if user is a TD (has hosted at least one event)
  useEffect(() => {
    const checkTD = async () => {
      if (!user?.uid) { setIsTD(false); return; }
      try {
        const { getDocs: gds, collection: col, query: q, where: w } = await import('firebase/firestore');
        const snap = await gds(q(col(db, 'events'), w('admin_uid', '==', user.uid)));
        setIsTD(snap.size > 0);
      } catch { setIsTD(false); }
    };
    checkTD();
  }, [user?.uid]);

  // If hire mode and event location provided, use it automatically
  useEffect(() => {
    if (mode === 'hire' && eventLat && eventLng) {
      setSearchLat(eventLat);
      setSearchLng(eventLng);
      runSearch(eventLat, eventLng);
    }
  }, [mode, eventLat, eventLng]);

  const geocodeCity = async () => {
    if (!searchCity.trim()) return;
    setGeocoding(true);
    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchCity)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      const data = await resp.json();
      if (data.results?.[0]?.geometry?.location) {
        const { lat, lng } = data.results[0].geometry.location;
        setSearchLat(lat);
        setSearchLng(lng);
        setSearchCity(data.results[0].formatted_address);
        runSearch(lat, lng);
      }
    } catch (e) { console.error(e); }
    setGeocoding(false);
  };

  const runSearch = async (lat: number, lng: number) => {
    setLoading(true);
    setSearched(true);
    try {
      const dealerSnap = await getDocs(collection(db, 'dealers'));
      const results: DealerResult[] = [];

      for (const d of dealerSnap.docs) {
        const dd = d.data();
        if (!dd.home_lat || !dd.home_lng) continue;
        if (dd.status !== 'active') continue;

        // Check home zone
        const homeDist = haversine(lat, lng, dd.home_lat, dd.home_lng);
        const withinSearch = homeDist <= searchRadius;
        const dealerWillTravel = homeDist <= (dd.travel_radius_km ?? 50);

        // Check travel zones
        let zoneMatch = false;
        try {
          const zonesSnap = await getDocs(collection(db, 'dealers', d.id, 'travel_zones'));
          const now = new Date();
          for (const z of zonesSnap.docs) {
            const zd = z.data();
            if (!zd.lat || !zd.lng) continue;
            const from = zd.available_from ? new Date(zd.available_from) : null;
            const until = zd.available_until ? new Date(zd.available_until) : null;
            const dateOk = (!from || from <= now) && (!until || until >= now);
            const zoneDist = haversine(lat, lng, zd.lat, zd.lng);
            if (dateOk && zoneDist <= (zd.radius_km ?? 50) && zoneDist <= searchRadius) {
              zoneMatch = true;
              break;
            }
          }
        } catch {}

        if (!withinSearch && !zoneMatch && !dealerWillTravel) continue;

        // Get user profile
        let displayName = 'Unknown Dealer';
        let photoURL = '';
        try {
          const userSnap = await getDocs(collection(db, 'users'));
          const userDoc = userSnap.docs.find(u => u.id === d.id);
          if (userDoc) {
            displayName = userDoc.data().display_name ?? userDoc.data().alias ?? 'Unknown';
            photoURL = userDoc.data().avatar_url ?? '';
          }
        } catch {}

        results.push({
          uid: d.id,
          displayName,
          photoURL,
          tier: dd.tier ?? 'pc_certified',
          track: dd.track ?? 'professional',
          availability: dd.availability ?? 'available',
          home_city: dd.home_city ?? '',
          home_lat: dd.home_lat,
          home_lng: dd.home_lng,
          travel_radius_km: dd.travel_radius_km ?? 50,
          avg_rating: dd.avg_rating ?? 0,
          rating_count: dd.rating_count ?? 0,
          response_rate: dd.response_rate ?? 100,
          event_count: dd.event_count ?? 0,
          cancellation_count: dd.cancellation_count ?? 0,
          late_cancellation_count: dd.late_cancellation_count ?? 0,
          distance_km: Math.round(homeDist),
          games: dd.games ?? ['holdem'],
        });
      }

      // Sort by distance
      results.sort((a, b) => a.distance_km - b.distance_km);
      setDealers(results);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendIntroduction = async (dealerUid: string) => {
    if (!user?.uid || inviteSent.has(dealerUid)) return;
    setInviteSending(dealerUid);
    try {
      await addDoc(collection(db, 'users', dealerUid, 'notifications'), {
        type: 'dealer_invite_received',
        recipientUid: dealerUid,
        eventId: '',
        eventName: 'Introduction',
        actorName: user.displayName ?? 'A Tournament Director',
        message: `${user.displayName ?? 'A TD'} is interested in working with you — they run games in your area and want to connect.`,
        urgent: false,
        read: false,
        createdAt: serverTimestamp(),
      });
      setInviteSent(s => new Set([...s, dealerUid]));
    } catch (e) { console.error(e); }
    setInviteSending(null);
  };

  const sendEventInvite = async (dealerUid: string) => {
    if (!user?.uid || !eventId || inviteSent.has(dealerUid)) return;
    setInviteSending(dealerUid);
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'events', eventId), {
        dealer_uid: dealerUid,
        dealer_status: 'pending',
        dealer_invite_deadline: inviteDeadline || new Date(Date.now() + 24*3600000).toISOString(),
        dealer_invite_urgent: false,
      });
      await addDoc(collection(db, 'users', dealerUid, 'notifications'), {
        type: 'dealer_invite_received',
        recipientUid: dealerUid,
        eventId,
        eventName: eventName ?? 'Poker Event',
        actorName: user.displayName ?? 'Tournament Director',
        message: `${user.displayName ?? 'A TD'} has invited you to deal at "${eventName ?? 'Poker Event'}"`,
        urgent: false,
        read: false,
        createdAt: serverTimestamp(),
      });
      setInviteSent(s => new Set([...s, dealerUid]));
      if (onDealerInvited) onDealerInvited(dealerUid);
    } catch (e) { console.error(e); }
    setInviteSending(null);
  };

  const filtered = dealers.filter(d => {
    if (trackFilter !== 'all' && d.track !== trackFilter) return false;
    if (availFilter === 'available' && d.availability !== 'available') return false;
    return true;
  });

  const F = { ui: "'Rajdhani','Arial',sans-serif", mono: "'Share Tech Mono',monospace" };
  const C = { bg: '#080A0E', card: '#131720', border: '#1E2330', gold: '#D4A017', teal: '#19E6CF', white: '#F0F4FF', ink: '#C8D0E0', dim: '#6B7590', green: '#22C55E', red: '#EF4444' };

  if (!tierLoading && !canFindDealers) return (
    <TierGate allowed={false} feature="Find a Dealer"
      requiredTier="pro" inline={false}><div /></TierGate>
  );

  return (
    <Box maxW="720px" mx="auto" px={4} py={8} fontFamily={F.ui}>

      {/* TD Gate */}
      {isTD === null && (
        <Center py={12}><Spinner color="teal.400" size="lg" /></Center>
      )}
      {isTD === false && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={8} textAlign="center">
          <Text style={{ fontSize: 36, marginBottom: 12 }}>🎖</Text>
          <Text style={{ fontFamily: F.ui, fontSize: 18, fontWeight: 700, color: C.white, marginBottom: 8 }}>
            Tournament Directors Only
          </Text>
          <Text style={{ fontFamily: F.ui, fontSize: 14, color: C.dim, lineHeight: 1.7, marginBottom: 24 }}>
            The dealer discovery page is available to Tournament Directors who have hosted at least one event on PokerCity.
            Create your first event to unlock this feature.
          </Text>
          <Box as="button" px={6} py={3} bg="#052E16" border={`1px solid #166534`}
            borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
            fontFamily={F.ui} cursor="pointer"
            onClick={() => navigate('/create-event/')}>
            Create an Event →
          </Box>
        </Box>
      )}
      {isTD === true && (<>

      {/* Header */}
      <Flex align="center" justify="space-between" mb={6}>
        <Box>
          <Text style={{ fontFamily: F.ui, fontSize: 26, fontWeight: 700, color: C.white }}>
            {mode === 'hire' ? 'Find a Dealer for Your Event' : 'Find a Dealer'}
          </Text>
          <Text style={{ fontFamily: F.ui, fontSize: 14, color: C.dim, marginTop: 4 }}>
            {mode === 'hire'
              ? `Browse PC certified dealers available near ${eventName ?? 'your event'}`
              : 'Browse PC certified dealers and connect with the network'}
          </Text>
        </Box>
        {mode === 'browse' && (
          <Box as="button" px={3} py={2} bg={C.card} border={`1px solid ${C.border}`}
            borderRadius={6} fontSize="12px" color={C.dim} fontFamily={F.mono}
            cursor="pointer" onClick={() => navigate(-1)}>
            ← Back
          </Box>
        )}
      </Flex>

      {/* Search bar */}
      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={4} mb={4}>
        <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.teal, letterSpacing: '0.2em', marginBottom: 12 }}>
          SEARCH LOCATION
        </Text>
        <Flex gap={2} mb={3} flexWrap="wrap">
          <input
            type="text"
            placeholder="Enter city, town, or address..."
            value={searchCity}
            onChange={e => setSearchCity(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && geocodeCity()}
            style={{ flex: 1, minWidth: 200, background: '#0D1118', border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '10px 14px', color: C.white, fontSize: 14,
              fontFamily: F.ui }}
          />
          <Box as="button" px={4} py={2} bg="#052E16" border={`1px solid #166534`}
            borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
            fontFamily={F.ui} cursor={geocoding ? 'wait' : 'pointer'}
            onClick={geocodeCity}>
            {geocoding ? 'Searching...' : 'Search'}
          </Box>
        </Flex>

        {/* Radius selector */}
        <Flex gap={2} flexWrap="wrap">
          <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.dim, letterSpacing: '0.1em', alignSelf: 'center' }}>
            RADIUS:
          </Text>
          {RADIUS_OPTIONS.map(r => (
            <Box key={r} as="button" px={3} py={1}
              bg={searchRadius === r ? '#0D1F1E' : C.card}
              border={`1px solid ${searchRadius === r ? C.teal : C.border}`}
              borderRadius={6} fontSize="12px" fontWeight="700"
              color={searchRadius === r ? C.teal : C.dim}
              fontFamily={F.mono} cursor="pointer"
              onClick={() => { setSearchRadius(r); if (searchLat && searchLng) runSearch(searchLat, searchLng); }}>
              {RADIUS_LABELS[r]}
            </Box>
          ))}
        </Flex>
      </Box>

      {/* Certification disclaimer — always visible */}
      <Box bg="#1A1200" border={`1px solid #D4A017`} borderRadius={8} p={4} mb={4}>
        <Flex align="flex-start" gap={3}>
          <Text style={{ fontSize: 18, flexShrink: 0 }}>⚠</Text>
          <Box>
            <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.gold, letterSpacing: '0.15em', marginBottom: 6 }}>
              ABOUT CERTIFICATION LEVELS
            </Text>
            <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.ink, lineHeight: 1.7 }}>
              PokerCity certification reflects a dealer's <b style={{ color: C.white }}>knowledge</b> of poker rules, TDA procedures,
              and dealing protocol — assessed through standardized testing. It does <b style={{ color: C.white }}>not</b> verify
              live dealing experience, speed, or performance under real event conditions.
            </Text>
            <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim, lineHeight: 1.7, marginTop: 6 }}>
              <b style={{ color: C.ink }}>Event count and TD ratings</b> are a more reliable indicator of practical experience.
              Dealers with 0 events are unverified in the field regardless of certification tier.
              A verified live experience program is in development.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Filters */}
      {searched && dealers.length > 0 && (
        <Flex gap={3} mb={4} flexWrap="wrap" align="center">
          <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.dim, letterSpacing: '0.1em' }}>FILTER:</Text>
          <Flex gap={2}>
            {(['all', 'professional', 'home'] as const).map(t => (
              <Box key={t} as="button" px={3} py={1}
                bg={trackFilter === t ? '#0D1F1E' : C.card}
                border={`1px solid ${trackFilter === t ? C.gold : C.border}`}
                borderRadius={6} fontSize="12px" fontWeight="700"
                color={trackFilter === t ? C.gold : C.dim}
                fontFamily={F.mono} cursor="pointer"
                onClick={() => setTrackFilter(t)}>
                {t === 'all' ? 'All Tracks' : t === 'professional' ? 'Professional' : 'Home Game'}
              </Box>
            ))}
          </Flex>
          <Flex gap={2}>
            {(['available', 'all'] as const).map(a => (
              <Box key={a} as="button" px={3} py={1}
                bg={availFilter === a ? '#0A1A10' : C.card}
                border={`1px solid ${availFilter === a ? C.green : C.border}`}
                borderRadius={6} fontSize="12px" fontWeight="700"
                color={availFilter === a ? C.green : C.dim}
                fontFamily={F.mono} cursor="pointer"
                onClick={() => setAvailFilter(a)}>
                {a === 'available' ? 'Available Only' : 'All'}
              </Box>
            ))}
          </Flex>
        </Flex>
      )}

      {/* Results */}
      {loading && <Center py={12}><Spinner color="teal.400" size="xl" /></Center>}

      {!loading && searched && filtered.length === 0 && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={8} textAlign="center">
          <Text style={{ fontSize: 32, marginBottom: 12 }}>🔍</Text>
          <Text style={{ fontFamily: F.ui, fontSize: 16, color: C.white, marginBottom: 8 }}>No dealers found</Text>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim }}>
            Try expanding your search radius or adjusting filters.
          </Text>
        </Box>
      )}

      {!loading && filtered.map(dealer => {
        const tier = TIER_CONFIG[dealer.tier] ?? TIER_CONFIG['pc_certified'];
        const avail = AVAIL_CONFIG[dealer.availability] ?? AVAIL_CONFIG['available'];
        const driveMin = kmToMinutes(dealer.distance_km);
        const alreadySent = inviteSent.has(dealer.uid);

        return (
          <Box key={dealer.uid} bg={C.card} border={`1px solid ${C.border}`}
            borderRadius={10} overflow="hidden" mb={3}
            _hover={{ borderColor: C.teal }} transition="border-color 0.2s">

            {/* Card header */}
            <Flex bg="#0D1520" px={4} py={2} align="center" justify="space-between">
              <Flex align="center" gap={2}>
                <Text style={{ fontFamily: F.mono, fontSize: 11, color: tier.color, letterSpacing: '0.15em' }}>
                  {tier.icon} {tier.label.toUpperCase()}
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w="7px" h="7px" borderRadius="full" bg={avail.color} />
                <Text style={{ fontFamily: F.mono, fontSize: 10, color: avail.color, letterSpacing: '0.12em' }}>
                  {avail.label.toUpperCase()}
                </Text>
              </Flex>
            </Flex>

            {/* Card body */}
            <Flex p={4} gap={4} align="flex-start">
              <Avatar size="md" src={dealer.photoURL} name={dealer.displayName} flexShrink={0} />

              <Box flex={1}>
                <Flex align="baseline" gap={3} mb={1} flexWrap="wrap">
                  <Text style={{ fontFamily: F.ui, fontSize: 18, fontWeight: 700, color: C.white }}>
                    {dealer.displayName}
                  </Text>
                  {dealer.event_count === 0 && (
                    <Box px={2} py={0} bg="#2D1F00" border={`1px solid #D4A017`} borderRadius={4}>
                      <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#D4A017', letterSpacing: '0.12em' }}>
                        NO LIVE EVENTS YET
                      </Text>
                    </Box>
                  )}
                  {dealer.avg_rating > 0 && (
                    <Text style={{ fontFamily: F.mono, fontSize: 12, color: C.gold }}>
                      ★ {dealer.avg_rating.toFixed(1)} ({dealer.rating_count})
                    </Text>
                  )}
                </Flex>

                <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim, marginBottom: 8 }}>
                  {dealer.home_city}
                  {dealer.distance_km > 0 && ` · ${dealer.distance_km} km away (~${driveMin} min)`}
                  {` · Will travel ${dealer.travel_radius_km === 9999 ? 'province-wide' : `${dealer.travel_radius_km} km`}`}
                </Text>

                {/* Stats */}
                <Flex gap={4} flexWrap="wrap" mb={3}>
                  <Box>
                    <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>RESPONSE</Text>
                    <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700,
                      color: (dealer.response_rate ?? 100) >= 90 ? C.green : (dealer.response_rate ?? 100) >= 70 ? C.gold : C.red }}>
                      {dealer.response_rate ?? 100}%
                    </Text>
                  </Box>
                  <Box>
                    <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>EVENTS</Text>
                    <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700, color: C.white }}>
                      {dealer.event_count}
                    </Text>
                  </Box>
                  {dealer.cancellation_count > 0 && (
                    <Box>
                      <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>CANCELLATIONS</Text>
                      <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700, color: C.gold }}>
                        {dealer.cancellation_count} ({dealer.late_cancellation_count} late)
                      </Text>
                    </Box>
                  )}
                  <Box>
                    <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>TRACK</Text>
                    <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700, color: C.ink }}>
                      {dealer.track === 'professional' ? 'Professional' : 'Home Game'}
                    </Text>
                  </Box>
                </Flex>

                {/* Actions */}
                <Flex gap={2} flexWrap="wrap">
                  <Box as="button" px={3} py={2} bg={C.card} border={`1px solid ${C.border}`}
                    borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
                    fontFamily={F.ui} cursor="pointer"
                    onClick={() => navigate(`/dealer/${dealer.uid}`)}>
                    View Passport →
                  </Box>

                  {mode === 'browse' && (
                    <Box as="button" px={3} py={2}
                      bg={alreadySent ? '#052E16' : '#0D1520'}
                      border={`1px solid ${alreadySent ? C.green : C.gold}`}
                      borderRadius={6} fontSize="12px" fontWeight="700"
                      color={alreadySent ? C.green : C.gold}
                      fontFamily={F.ui} cursor={alreadySent ? 'default' : 'pointer'}
                      onClick={() => !alreadySent && sendIntroduction(dealer.uid)}>
                      {invitingSending === dealer.uid ? 'Sending...' : alreadySent ? '✓ Introduction Sent' : 'Send Introduction'}
                    </Box>
                  )}

                  {mode === 'hire' && (
                    <Box as="button" px={3} py={2}
                      bg={alreadySent ? '#052E16' : '#0D1F1E'}
                      border={`1px solid ${alreadySent ? C.green : C.teal}`}
                      borderRadius={6} fontSize="12px" fontWeight="700"
                      color={alreadySent ? C.green : C.teal}
                      fontFamily={F.ui} cursor={alreadySent ? 'default' : 'pointer'}
                      onClick={() => !alreadySent && sendEventInvite(dealer.uid)}>
                      {invitingSending === dealer.uid ? 'Inviting...' : alreadySent ? '✓ Invited' : 'Invite to Event'}
                    </Box>
                  )}
                </Flex>
              </Box>
            </Flex>
          </Box>
        );
      })}

      {!searched && !loading && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={8} textAlign="center">
          <Text style={{ fontSize: 36, marginBottom: 12 }}>🗺</Text>
          <Text style={{ fontFamily: F.ui, fontSize: 16, color: C.white, marginBottom: 8 }}>
            Enter a location to find dealers
          </Text>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim }}>
            Search by city, town, or address. Dealers within your selected radius will appear here.
          </Text>
        </Box>
      )}
      </>)}
    </Box>
  );
};

export default DealerDiscoveryPage;
